'use client'

// Conversational QnA marketing block. Three industry variants render
// as side-by-side product cards with chip strips and animated chat
// previews. Inspired by iAdvize's "Product Expert" / "Conversation
// Starters", a chip strip of pre-baked questions plus a curated chat.
import Link from 'next/link'
import { useEffect, useMemo, useState } from 'react'
import { urlFor } from '@/lib/sanity/image'

type Industry = 'retail' | 'travel' | 'insurance'
type ChatTurn = { role: 'user' | 'assistant'; text: string }

type SanityImage = { asset?: { _ref?: string; _id?: string } }

export type RedesignQnaVariant = {
  industry: Industry
  productName: string
  productImage?: SanityImage
  productPrice?: string
  chipQuestions?: string[]
  sampleConversation?: ChatTurn[]
  accentColor?: string
}

export type RedesignQnaBlockProps = {
  title?: string
  subtitle?: string
  variants?: RedesignQnaVariant[]
  ctaLabel?: string
  ctaUrl?: string
}

const FALLBACK_IMAGES: Record<Industry, string> = {
  retail:
    'https://images.unsplash.com/photo-1542291026-7eec264c27ff?auto=format&fit=crop&w=800&h=600&q=80',
  travel:
    'https://images.unsplash.com/photo-1539020140153-e479b8c22e70?auto=format&fit=crop&w=800&h=600&q=80',
  insurance:
    'https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?auto=format&fit=crop&w=800&h=600&q=80',
}

const DEFAULT_VARIANTS: RedesignQnaVariant[] = [
  {
    industry: 'retail',
    productName: 'Allbirds Wool Runners',
    productPrice: '$129',
    chipQuestions: [
      'Are these machine washable?',
      "What's the difference between sizes?",
      'Do they run small?',
      'Vegan friendly?',
    ],
    sampleConversation: [
      { role: 'user', text: 'Are these machine washable?' },
      { role: 'assistant', text: 'Yes, cold cycle, remove the laces and insoles first. Air dry only.' },
      { role: 'user', text: 'Do they run small?' },
      { role: 'assistant', text: 'They run true-to-size for most. If you are between sizes, size up half.' },
    ],
  },
  {
    industry: 'travel',
    productName: 'Riads of Marrakech, 3-night stay',
    productPrice: 'From €45/night',
    chipQuestions: [
      'Is breakfast included?',
      'How far from the Medina?',
      'Best months to visit?',
      'Family-friendly?',
    ],
    sampleConversation: [
      { role: 'user', text: 'Is breakfast included?' },
      { role: 'assistant', text: 'Yes, traditional Moroccan breakfast on the rooftop, included for all guests.' },
      { role: 'user', text: 'How far from the Medina?' },
      { role: 'assistant', text: '4 minutes on foot. The riad is inside the old city walls.' },
    ],
  },
  {
    industry: 'insurance',
    productName: 'Comprehensive Auto Cover',
    productPrice: 'From $39/month',
    chipQuestions: [
      "What's covered for theft?",
      'Is roadside assistance included?',
      'Can I add a second driver?',
      "What's the excess?",
    ],
    sampleConversation: [
      { role: 'user', text: "What's covered for theft?" },
      { role: 'assistant', text: 'Full vehicle value at current market rate, plus personal items up to $500.' },
      { role: 'user', text: 'Is roadside assistance included?' },
      { role: 'assistant', text: 'Yes: 24/7 roadside, towing up to 50 miles, no extra cost.' },
    ],
  },
]

function resolveImage(variant: RedesignQnaVariant): { src: string; alt: string } {
  const ref = variant.productImage?.asset?._ref || variant.productImage?.asset?._id
  if (ref) {
    return {
      src: urlFor(variant.productImage as Parameters<typeof urlFor>[0]).width(800).height(600).url(),
      alt: variant.productName,
    }
  }
  return { src: FALLBACK_IMAGES[variant.industry], alt: variant.productName }
}

type AnimatedChatProps = {
  turns: ChatTurn[]
  accent: string
}

function AnimatedChat({ turns, accent }: AnimatedChatProps) {
  const safeTurns = useMemo(() => (turns.length ? turns : []), [turns])
  const [visibleCount, setVisibleCount] = useState(0)
  const [typing, setTyping] = useState(false)

  useEffect(() => {
    if (!safeTurns.length) return
    let cancelled = false
    const sequence = async () => {
      while (!cancelled) {
        for (let i = 0; i < safeTurns.length; i++) {
          if (cancelled) return
          if (safeTurns[i].role === 'assistant') {
            setTyping(true)
            await wait(900)
            if (cancelled) return
            setTyping(false)
          }
          setVisibleCount(i + 1)
          await wait(1400)
          if (cancelled) return
        }
        await wait(2200)
        if (cancelled) return
        setVisibleCount(0)
      }
    }
    sequence()
    return () => {
      cancelled = true
    }
  }, [safeTurns])

  const shown = safeTurns.slice(0, visibleCount)
  const nextIsAssistantPending =
    typing && visibleCount < safeTurns.length && safeTurns[visibleCount]?.role === 'assistant'

  return (
    <div
      aria-live="polite"
      className="qna-chat"
      style={{
        background: 'var(--bg-2)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-lg)',
        padding: 14,
        minHeight: 200,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        overflow: 'hidden',
      }}
    >
      {shown.map((turn, i) => (
        <div
          key={`${i}-${turn.role}`}
          style={{
            alignSelf: turn.role === 'user' ? 'flex-end' : 'flex-start',
            maxWidth: '85%',
            background: turn.role === 'user' ? accent : 'white',
            color: turn.role === 'user' ? 'white' : 'var(--ink)',
            border: turn.role === 'user' ? '1px solid transparent' : '1px solid var(--border)',
            borderRadius: turn.role === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
            padding: '8px 12px',
            fontSize: 13,
            lineHeight: 1.4,
            boxShadow: 'var(--shadow-xs)',
            animation: 'qna-bubble-in 280ms var(--ease, ease) both',
          }}
        >
          {turn.text}
        </div>
      ))}
      {nextIsAssistantPending && (
        <div
          aria-hidden
          style={{
            alignSelf: 'flex-start',
            background: 'white',
            border: '1px solid var(--border)',
            borderRadius: '14px 14px 14px 4px',
            padding: '10px 12px',
            display: 'inline-flex',
            gap: 4,
            alignItems: 'center',
          }}
        >
          <span className="qna-dot" />
          <span className="qna-dot" style={{ animationDelay: '120ms' }} />
          <span className="qna-dot" style={{ animationDelay: '240ms' }} />
        </div>
      )}
    </div>
  )
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms)
  })
}

const INDUSTRY_LABEL: Record<Industry, string> = {
  retail: 'Retail',
  travel: 'Travel',
  insurance: 'Insurance',
}

export function RedesignQnaBlock(props: RedesignQnaBlockProps = {}) {
  const variants = props.variants?.length ? props.variants : DEFAULT_VARIANTS
  const title = props.title ?? 'Answer every question, before they ask.'
  const subtitle =
    props.subtitle ??
    'A chip-strip of curated questions, an answer in two seconds, and the right product front-and-centre. Works the same way across retail, travel and insurance.'
  const ctaLabel = props.ctaLabel ?? 'Request demo'
  const ctaUrl = props.ctaUrl ?? '/request-demo'

  return (
    <section className="section" style={{ fontFamily: 'var(--font-body)' }}>
      <style>{KEYFRAMES_CSS}</style>
      <div className="container">
        <div className="text-center" style={{ marginBottom: 'var(--s-10)' }}>
          <div className="eyebrow" style={{ justifyContent: 'center' }}>
            <span className="dot" />
            Conversational QnA
          </div>
          <h2 className="h2" style={{ marginTop: 14, maxWidth: '24ch', marginInline: 'auto' }}>
            {title}
          </h2>
          {subtitle && (
            <p className="lead" style={{ marginTop: 14, marginInline: 'auto' }}>
              {subtitle}
            </p>
          )}
        </div>

        <div className="qna-grid">
          {variants.map((variant, i) => {
            const img = resolveImage(variant)
            const accent = variant.accentColor || 'var(--blue)'
            return (
              <article
                key={`${variant.industry}-${i}`}
                className="qna-card"
                style={{
                  background: 'white',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--r-card)',
                  padding: 18,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 14,
                  boxShadow: 'var(--shadow-sm)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span
                    className="lime-pill"
                    style={{ background: 'var(--lime-50)', color: 'var(--ink)' }}
                  >
                    {INDUSTRY_LABEL[variant.industry]}
                  </span>
                  {variant.productPrice && (
                    <span style={{ fontWeight: 600, fontSize: 14 }}>{variant.productPrice}</span>
                  )}
                </div>

                <div
                  style={{
                    aspectRatio: '4 / 3',
                    borderRadius: 'var(--r-md)',
                    overflow: 'hidden',
                    background: 'var(--bg-2)',
                  }}
                >
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={img.src}
                    alt={img.alt}
                    loading="lazy"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </div>

                <h3 className="h4" style={{ margin: 0 }}>{variant.productName}</h3>

                {variant.chipQuestions && variant.chipQuestions.length > 0 && (
                  <ul
                    className="qna-chips"
                    style={{
                      listStyle: 'none',
                      padding: 0,
                      margin: 0,
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 6,
                    }}
                  >
                    {variant.chipQuestions.slice(0, 5).map((q, qi) => (
                      <li key={qi}>
                        <button
                          type="button"
                          aria-disabled="true"
                          className="qna-chip"
                          style={{
                            background: 'white',
                            border: '1px solid var(--border-strong)',
                            color: 'var(--ink)',
                            fontSize: 12,
                            fontWeight: 500,
                            padding: '6px 10px',
                            borderRadius: 'var(--r-pill)',
                            cursor: 'default',
                            transition: 'transform var(--dur-fast) var(--ease), box-shadow var(--dur-fast) var(--ease), border-color var(--dur-fast) var(--ease)',
                          }}
                        >
                          {q}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}

                <AnimatedChat turns={variant.sampleConversation ?? []} accent={accent} />
              </article>
            )
          })}
        </div>

        <div style={{ textAlign: 'center', marginTop: 'var(--s-12)' }}>
          <Link className="btn btn-primary btn-lg" href={ctaUrl}>
            {ctaLabel} →
          </Link>
        </div>
      </div>
    </section>
  )
}

const KEYFRAMES_CSS = `
  .qna-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: var(--s-5); }
  @media (max-width: 1024px) { .qna-grid { grid-template-columns: repeat(2, 1fr); } }
  @media (max-width: 720px)  { .qna-grid { grid-template-columns: 1fr; } }
  .qna-chip:hover { transform: translateY(-1px); box-shadow: var(--shadow-sm); border-color: var(--blue); }
  .qna-dot {
    width: 5px; height: 5px; border-radius: 50%;
    background: var(--fg-3); display: inline-block;
    animation: qna-dot-pulse 900ms infinite ease-in-out;
  }
  @keyframes qna-dot-pulse {
    0%, 60%, 100% { opacity: 0.25; transform: translateY(0); }
    30% { opacity: 1; transform: translateY(-2px); }
  }
  @keyframes qna-bubble-in {
    from { opacity: 0; transform: translateY(4px); }
    to   { opacity: 1; transform: translateY(0); }
  }
`
